import React, {useEffect, useState} from "react";
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Radio, Stack, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {UserGroup03Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {groupCount, WebrtcRepository, webrtcErrorMessage} from "../../repositories/WebrtcRepository";

// "Send to group" on a live conversation: the call is transferred to an agent group and rings every joined agent.
// Works with nobody online (the call goes to the group's transfer number). onDone(response): after the handover.
export const SendToGroupButton = ({projectId, conversationId, disabled, sx, onDone, iconOnly = false}) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    setGroups(null);
    setGroupId(null);
    WebrtcRepository.getGroups({projectId})
      .then(({data}) => {
        const list = data?.groups || [];
        setGroups(list);
        if (list.length === 1) setGroupId(list[0].id);
      })
      .catch(e => {
        setGroups([]);
        toast.error(webrtcErrorMessage(e, "Failed to load the agent groups"));
      });
  }, [open, projectId]);

  const send = async () => {
    const group = groups?.find(item => item.id === groupId);
    if (!group) return;
    setSending(true);
    try {
      const response = await WebrtcRepository.handoverToGroup({projectId, conversationId, groupId});
      toast.success(`Sent to "${group.name}"`);
      setOpen(false);
      onDone?.(response);
    } catch (e) {
      toast.error(webrtcErrorMessage(e, `Failed to send the call to "${group.name}"`));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip title={"Send to group"}>
          <span>
            <IconButton aria-label={"Send to group"} onClick={() => setOpen(true)} disabled={disabled} sx={sx}>
              <HugeiconsIcon icon={UserGroup03Icon} size={16}/>
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          size={"small"}
          onClick={() => setOpen(true)} disabled={disabled}
          startIcon={<HugeiconsIcon icon={UserGroup03Icon} size={16}/>} sx={sx}
        >
          Send to group
        </Button>
      )}
      <Dialog open={open} onClose={sending ? undefined : () => setOpen(false)} fullWidth maxWidth={"xs"}
              PaperProps={{sx: {borderRadius: "18px"}}}>
        <DialogTitle sx={{fontSize: 17, fontWeight: 700, color: "#23264D", pb: 0.5}}>Send to group</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize: 13, color: "#8A8AA0", mb: 1.5}}>
            The call rings every agent who joined the group. If nobody answers, it goes to the group's transfer number.
          </Typography>
          {groups === null && <Stack alignItems={"center"} sx={{py: 2}}><CircularProgress size={20}/></Stack>}
          {groups?.length === 0 && (
            <Typography sx={{fontSize: 13, color: "#8A8AA0", py: 1}}>
              No agent groups in this project. Add one in Settings → Agent Groups.
            </Typography>
          )}
          <Stack gap={0.5}>
            {groups?.map(group => (
              <Stack
                key={group.id} direction={"row"} alignItems={"center"} gap={1} onClick={() => setGroupId(group.id)}
                sx={{
                  px: 1, py: 0.75, borderRadius: "10px", cursor: "pointer",
                  bgcolor: groupId === group.id ? "rgba(132, 145, 247, 0.12)" : "transparent",
                  "&:hover": {bgcolor: groupId === group.id ? "rgba(132, 145, 247, 0.16)" : "#FAFAFD"},
                }}
              >
                <Radio size={"small"} checked={groupId === group.id} sx={{p: 0.5}}/>
                <Stack sx={{flex: 1, minWidth: 0}}>
                  <Typography noWrap sx={{fontSize: 13, fontWeight: 700, color: "#23264D"}}>{group.name}</Typography>
                  <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                    {groupCount(group.online_count)} online · {groupCount(group.waiting_count)} waiting
                    {group.online_count === 0 && group.transfer_number ? ` · goes to ${group.transfer_number}` : ""}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 2.5}}>
          <Button onClick={() => setOpen(false)} disabled={sending}
                  sx={{textTransform: "none", fontWeight: 600, color: "#4A4A63", borderRadius: "10px"}}>
            Cancel
          </Button>
          <Button
            onClick={send} variant={"contained"} disabled={!groupId || sending}
            startIcon={sending ? <CircularProgress size={14} color={"inherit"}/> : null}
            sx={{textTransform: "none", fontWeight: 600, borderRadius: "10px", boxShadow: "none", bgcolor: "#23264D",
              "&:hover": {bgcolor: "#33376B", boxShadow: "none"}}}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
